"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { Language, SUPPORTED_LANGUAGES, getTranslations } from '@/lib/i18n';

interface HeaderProps {
  language: Language;
  onLanguageChange: (lang: Language) => void;
}

export default function Header({ language, onLanguageChange }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const pathname = usePathname();
  const t = getTranslations(language);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav_homepage', label: t.navigation.home, href: '/homepage' },
    { id: 'nav_menu', label: t.navigation.menu, href: '/menu' },
    { id: 'nav_about', label: t.navigation.about, href: '/about' },
  ];

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center transition-transform group-hover:scale-110">
            <Icon name="HomeIcon" size={20} variant="solid" />
          </div>
          <span className="font-serif text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            RestaurantBrand
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks?.map((link) => (
            <Link
              key={link?.id}
              href={link?.href}
              className={`text-sm font-medium uppercase tracking-widest transition-colors relative ${
                pathname === link?.href
                  ? 'text-primary after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-primary'
                  : 'text-muted-foreground hover:text-primary'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* CTA + Language Selector */}
        <div className="hidden md:flex items-center gap-4">
          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-sm font-bold text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              aria-label="Select language"
            >
              <Icon name="GlobeAltIcon" size={16} />
              <span>{currentLang?.nativeLabel}</span>
              <Icon name="ChevronDownIcon" size={14} />
            </button>
            {isLangOpen && (
              <div className="absolute right-0 top-full mt-2 bg-card border border-border rounded-2xl shadow-xl overflow-hidden z-50 min-w-[140px]">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { onLanguageChange(lang.code); setIsLangOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-primary/10 hover:text-primary ${
                      language === lang.code ? 'text-primary bg-primary/5' : 'text-foreground'
                    }`}
                  >
                    <span className="font-bold w-6 text-center">{lang.nativeLabel}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="tel:5551234567"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Icon name="PhoneIcon" size={18} />
            <span>{t.navigation.phone}</span>
          </a>
          <Link
            href="/homepage#reservation"
            className="px-6 py-3 bg-primary text-primary-foreground rounded-full text-sm font-bold uppercase tracking-wider hover:scale-105 active:scale-95 transition-transform shadow-lg"
          >
            {t.navigation.reserveTable}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 text-foreground"
          aria-label="Toggle menu"
        >
          <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[72px] bg-background z-40 animate-fade-in">
          <nav className="flex flex-col items-center gap-8 py-12">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-lg font-medium uppercase tracking-widest transition-colors ${
                  pathname === link?.href ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {link?.label}
              </Link>
            ))}

            {/* Mobile Language Selector */}
            <div className="flex items-center gap-2 mt-2">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { onLanguageChange(lang.code); setIsMobileMenuOpen(false); }}
                  className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                    language === lang.code
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                  }`}
                >
                  {lang.nativeLabel}
                </button>
              ))}
            </div>

            <a
              href="tel:5551234567"
              className="flex items-center gap-2 text-lg text-muted-foreground mt-4"
            >
              <Icon name="PhoneIcon" size={20} />
              <span>{t.navigation.phone}</span>
            </a>
            <Link
              href="/homepage#reservation"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-8 py-4 bg-primary text-primary-foreground rounded-full text-base font-bold uppercase tracking-wider mt-4"
            >
              {t.navigation.reserveTable}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}